import { ArrowForward, ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, ButtonGroup, Grid, IconButton, Paper, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridOverlay, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Api from "../../Api";
import { Feature, PermissionContext } from "../../contexts/PermissionContext";
import { IContractVariation, IContractVariations, IContractVariationTableOptions } from "../../interfaces";
import { TabPanel } from "../Shared/TabPanel";


export const ContractVariations: React.FC<IContractVariations> = ({
    selectedContract,
    searchQuery,
    onShowAll,
    ...props }) => {

    const [value, setValue] = useState(0);
    const navigate = useNavigate();
    const permissionContext = useContext(PermissionContext);

    return (
        <Accordion defaultExpanded={true} color="primary" disableGutters >

            <AccordionSummary expandIcon={<ExpandMore />} >
                Contract / Lease Variations
            </AccordionSummary>

            <AccordionDetails>

                {selectedContract &&
                    <Typography variant="subtitle2" sx={{ mb: 1, float: "right" }}>
                        Selected contract / lease

                        <ButtonGroup sx={{ ml: 2 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => navigate("/contract/update/" + selectedContract.id)}
                            >
                                View {selectedContract.value}
                            </Button>


                            <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                onClick={onShowAll}
                            >
                                Clear
                            </Button>
                        </ButtonGroup>

                    </Typography>



                }
                {permissionContext.isPermitted(Feature.ContractVariation_Create) && (

                    selectedContract &&
                    <Tooltip title={`contract / lease variation for ${selectedContract.value}`}>
                        <span>
                            <Button
                                variant="contained"
                                sx={{ ml: 2 }}
                                onClick={() => navigate("/contractvariation/" + selectedContract.id)}
                            >
                                New Contract / Lease Variation
                            </Button>
                        </span>
                    </Tooltip>

                )}
                {selectedContract ?

                    <Grid container sx={{ my: 3 }}>
                        <Grid item xs={12}>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                <Tabs value={value} onChange={(_, value) => setValue(value)} aria-label="cv-table-tabs">
                                    <Tab
                                        label="Open"
                                        id={`cv-table-tab-${0}`} />
                                    <Tab
                                        label="Closed"
                                        id={`cv-table-tab-${1}`}
                                    />
                                </Tabs>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <TabPanel value={value} index={0}>
                                <VariationsTable
                                    contractId={selectedContract.id ?? ""}
                                    searchQuery={selectedContract.value ? selectedContract.value : searchQuery}
                                    open={true}
                                />
                            </TabPanel>
                            <TabPanel value={value} index={1}>
                                <VariationsTable
                                    contractId={selectedContract.id ?? ""}
                                    searchQuery={selectedContract.value ? selectedContract.value : searchQuery}
                                    open={false}
                                />
                            </TabPanel>
                        </Grid>

                    </Grid>
                    : <Typography variant="subtitle2">Please select a contract to view its variations.</Typography>
                }

            </AccordionDetails>
        </Accordion>
    );
}

export const VariationsTable: React.FC<IContractVariationTableOptions> = ({
    contractId,
    searchQuery,
    open,
    ...props }) => {


    const [contractVariations, setContractVariations] = useState<IContractVariation[]>([]);
    const [dataLoading, setDataLoading] = useState<boolean>(true);

    useEffect(() => {

        Api.Contracts.Variation.getByStatus(contractId, open ? "open" : "closed").then(x => {


            console.log(x);


            setContractVariations(x as IContractVariation[])
            setDataLoading(false);
        });

    }, [contractId]);

    useEffect(() => {


    }, [contractVariations, dataLoading])

    return (
        <Grid container sx={{ height: '58vh' }}>
            <Paper sx={{ width: '100%' }} elevation={0}>
                <DataGrid
                    rows={variationRows(contractVariations, searchQuery)}
                    getRowId={(row) => row.contractVariationId}
                    columns={variationColumns}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    components={{ NoRowsOverlay: () => emptyVariationsTable(dataLoading) }}
                    loading={dataLoading}
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'dateCreated', sort: 'desc' }]
                        },
                        pagination: {

                            pageSize: 10

                        }
                    }}
                />
            </Paper>

        </Grid>

    );
}

const emptyVariationsTable = (dataLoading: boolean) => {
    return <GridOverlay> {dataLoading ? "Loading contract variations." : "No contract variations."}</GridOverlay>
}

const variationColumns: GridColDef[] = [
    {
        field: 'cvNumber', headerName: 'CVNumber', flex: 1, renderCell: (params: GridRenderCellParams) => {
            return <Link to={`/contractvariation/update/${params.row.contract.contractId}/${params.row.contractVariationId}`}>{params.row.cvNumber ?? "No CV Number"}</Link>
        }
    },
    { field: 'statusName', headerName: 'Status', flex: 2 },

    {
        field: 'classes', headerName: 'Vehicle Class', flex: 1, renderCell: (params: GridRenderCellParams) => {
            return <Tooltip title={params.row.contract.classes}>
                <span>{params.row.contract.classes}</span>
            </Tooltip>
        }
    },
    { field: 'requestor', headerName: 'Requested By', flex: 1 },
    {
        field: 'contractManager', headerName: 'Procurement Manager', flex: 1, renderCell: (params: GridRenderCellParams) => {
            return <span>{params.row.contract.contractManager ? params.row.contract.contractManager.value : ``}</span>
        }
    },
    {
        field: 'categoryManager', headerName: 'Commercial Manager', flex: 1, renderCell: (params: GridRenderCellParams) => {
            return <span>{params.row.contract.categoryManager ? params.row.contract.categoryManager.value : ``}</span>
        }
    },
    {
        field: 'dateCreated',
        headerName: 'Created',
        flex: 0,
        renderCell: (params) => { return (params.row?.dateCreated) ? new Date(params.row?.dateCreated).toLocaleDateString('en-gb') : `` }

    },
    {
        field: 'edit', headerName: '', renderCell: (params: GridRenderCellParams) => {
            return <IconButton component={Link} to={`/contractvariation/update/${params.row.contract.contractId}/${params.row.contractVariationId}`}>
                <ArrowForward />
            </IconButton>
        }
    }
];

const variationRows = (cvs: IContractVariation[], searchQuery: string) => {

    return cvs;


    //return cvs.filter(x => {
    //    let queryable = `${x.cvNumber} ${x.statusName} ${new Date(x.dateCreated ?? "").toLocaleDateString('en-gb')}`;
    //    return queryable.includes(searchQuery);
    //});
}
